/**
 * Types: Configuração Unificada de Metas + Comissões para Luanne
 */

import { formatProductionPeriodName, getProductionPeriod } from '@/utils/productionPeriod';

export type TipoDesempenho =
  | 'RELACIONADOR'
  | 'LIDER_VENDAS'
  | 'SUPERVISOR'
  | 'POS_VENDA'
  | 'REPRESENTANTE'
  | 'ASSOCIADO';

export type GrupoDesempenho =
  | 'time_interno'
  | 'representacao'
  | 'associado';

/** Enum legado de comissões (comissoes_tipo_comissao_enum) */
export type TipoComissaoLegado =
  | 'RELACIONADOR'
  | 'LIDER'
  | 'SUPERVISOR'
  | 'POS_VENDA'
  | 'REPRESENTANTE'
  | 'ASSOCIADO';

export function tipoComissaoToDesempenho(tipo: TipoComissaoLegado): TipoDesempenho {
  return tipo === 'LIDER' ? 'LIDER_VENDAS' : tipo;
}

export function tipoDesempenhoToComissao(tipo: TipoDesempenho): TipoComissaoLegado {
  return tipo === 'LIDER_VENDAS' ? 'LIDER' : tipo;
}

export type DesempenhoNivel = 'DIAMANTE' | 'OURO' | 'PLATINUM' | 'BRONZE';

export interface DesempenhoFaixa {
  id: string;
  desempenho_config_id: string;
  nivel: DesempenhoNivel;
  classificacao: string;
  ordem: number;
  meta_valor?: number;
  meta_reunioes?: number;
  meta_fixacao?: number;
  bonus_percentual?: number;
  created_at: Date;
  updated_at: Date;
}

export interface DesempenhoConfig {
  id: string;
  tenant_id: number;
  /** Ausente para a meta padrão do cargo; preenchido para override individual. */
  profile_id?: string;
  grupo: GrupoDesempenho;
  tipo_desempenho: TipoDesempenho;

  // METAS
  meta_valor?: number;           // R$ (líderes/supervisores)
  meta_reunioes?: number;        // nº (relacionadores)
  meta_fixacao?: number;         // % (pós-venda)

  // COMISSÕES
  comissao_percentual_base: number; // % fixo; não inclui bônus
  /** @deprecated Legado: bônus não compõe a comissão. */
  comissao_bonus_percentual?: number;
  comissao_primeira_parcela_percentual?: number;
  comissao_parcelas_2_a_5_percentual?: number;
  comissao_gerente_pos_percentual?: number;

  // Período de produção (vigência)
  periodo_mes: number;
  periodo_ano: number;
  data_inicio: Date;
  data_fim?: Date;
  ativo: boolean;
  observacao?: string;

  created_at: Date;
  updated_at: Date;
}

export interface DesempenhoFaixaFormData {
  nivel: DesempenhoNivel;
  classificacao: string;
  ordem: number;
  meta_valor?: number;
  meta_reunioes?: number;
  meta_fixacao?: number;
  bonus_percentual?: number;
}

export interface DesempenhoConfigFormData {
  profile_id?: string | null;
  grupo: GrupoDesempenho;
  tipo_desempenho: TipoDesempenho;
  meta_valor?: number;
  meta_reunioes?: number;
  meta_fixacao?: number;
  comissao_percentual_base: number;
  /** @deprecated Legado: bônus não compõe a comissão. */
  comissao_bonus_percentual?: number;
  comissao_primeira_parcela_percentual?: number;
  comissao_parcelas_2_a_5_percentual?: number;
  comissao_gerente_pos_percentual?: number;
  periodo_mes: number;
  periodo_ano: number;
  ativo: boolean;
  observacao?: string;
}

export interface DesempenhoMembro {
  id: string;
  nome: string;
  role: string;
  tenant_id: number;
  status?: string;
}

export function formatPeriodoProducao(periodo_mes: number, periodo_ano: number): string {
  return formatProductionPeriodName(periodo_mes - 1, periodo_ano);
}

export function vigenciaFromPeriodo(
  periodo_mes: number,
  periodo_ano: number,
  startDay: number = 1
): { data_inicio: string; data_fim: string } {
  const { startDate, endDate } = getProductionPeriod(periodo_mes - 1, periodo_ano, startDay);
  return {
    data_inicio: startDate.toISOString().split('T')[0],
    data_fim: endDate.toISOString().split('T')[0],
  };
}

export function periodoProducaoAtual(): { periodo_mes: number; periodo_ano: number } {
  const now = new Date();
  return {
    periodo_mes: now.getMonth() + 1,
    periodo_ano: now.getFullYear(),
  };
}

export const NIVEL_DESEMPENHO_LABELS: Record<DesempenhoNivel, string> = {
  DIAMANTE: 'Diamante (AAA)',
  OURO: 'Ouro (A)',
  PLATINUM: 'Platinum (BB)',
  BRONZE: 'Bronze (B)',
};

export function labelFaixa(nivel?: DesempenhoNivel | string | null): string {
  if (!nivel) return '';
  return NIVEL_DESEMPENHO_LABELS[nivel as DesempenhoNivel] ?? String(nivel);
}

export function labelFaixaCurta(nivel?: DesempenhoNivel | string | null): string {
  const full = labelFaixa(nivel);
  if (!full) return 'Batida';
  return full.split(' ')[0];
}

// LABELS
export const TIPO_DESEMPENHO_LABELS: Record<TipoDesempenho, string> = {
  RELACIONADOR: 'Relacionador',
  LIDER_VENDAS: 'Líder de Vendas',
  SUPERVISOR: 'Supervisor',
  POS_VENDA: 'Especialista Pós-venda',
  REPRESENTANTE: 'Representante',
  ASSOCIADO: 'Associado',
};

export const GRUPO_DESEMPENHO_LABELS: Record<GrupoDesempenho, string> = {
  time_interno: 'Time Interno',
  representacao: 'Representação',
  associado: 'Associado',
};

export function tipoUsaFaixas(tipo: TipoDesempenho): boolean {
  return tipo !== 'ASSOCIADO';
}

export function resumoMetaFromFaixas(
  tipo: TipoDesempenho,
  faixas: DesempenhoFaixaFormData[]
): Pick<DesempenhoConfigFormData, 'meta_valor' | 'meta_reunioes' | 'meta_fixacao'> {
  const diamante = faixas.find(f => f.ordem === 1);
  if (!diamante) return {};

  if (tipo === 'RELACIONADOR') {
    return { meta_valor: diamante.meta_valor, meta_reunioes: diamante.meta_reunioes };
  }
  if (tipo === 'POS_VENDA') {
    return { meta_fixacao: diamante.meta_fixacao };
  }
  return { meta_valor: diamante.meta_valor };
}

// DEFAULTS (metas.txt — campanha 2026)
export const FAIXAS_DEFAULT_POR_TIPO: Record<
  Exclude<TipoDesempenho, 'ASSOCIADO'>,
  DesempenhoFaixaFormData[]
> = {
  SUPERVISOR: [
    { nivel: 'DIAMANTE', classificacao: 'AAA', ordem: 1, meta_valor: 5_000_000, bonus_percentual: 60 },
    { nivel: 'OURO', classificacao: 'A', ordem: 2, meta_valor: 4_000_000, bonus_percentual: 40 },
    { nivel: 'PLATINUM', classificacao: 'BB', ordem: 3, meta_valor: 3_000_000, bonus_percentual: 30 },
    { nivel: 'BRONZE', classificacao: 'B', ordem: 4, meta_valor: 2_000_000, bonus_percentual: 20 },
  ],
  RELACIONADOR: [
    { nivel: 'DIAMANTE', classificacao: 'AAA', ordem: 1, meta_valor: 2_000_000, meta_reunioes: 50 },
    { nivel: 'OURO', classificacao: 'A', ordem: 2, meta_valor: 1_500_000, meta_reunioes: 30 },
    { nivel: 'PLATINUM', classificacao: 'BB', ordem: 3, meta_valor: 1_000_000, meta_reunioes: 20 },
    { nivel: 'BRONZE', classificacao: 'B', ordem: 4, meta_valor: 800_000, meta_reunioes: 10 },
  ],
  LIDER_VENDAS: [
    { nivel: 'DIAMANTE', classificacao: 'AAA', ordem: 1, meta_valor: 2_000_000, bonus_percentual: 70 },
    { nivel: 'OURO', classificacao: 'A', ordem: 2, meta_valor: 1_500_000, bonus_percentual: 60 },
    { nivel: 'PLATINUM', classificacao: 'BB', ordem: 3, meta_valor: 1_000_000, bonus_percentual: 50 },
    { nivel: 'BRONZE', classificacao: 'B', ordem: 4, meta_valor: 800_000, bonus_percentual: 35 },
  ],
  REPRESENTANTE: [
    { nivel: 'DIAMANTE', classificacao: 'AAA', ordem: 1, meta_valor: 5_000_000, bonus_percentual: 60 },
    { nivel: 'OURO', classificacao: 'A', ordem: 2, meta_valor: 4_000_000, bonus_percentual: 40 },
    { nivel: 'PLATINUM', classificacao: 'BB', ordem: 3, meta_valor: 3_000_000, bonus_percentual: 30 },
    { nivel: 'BRONZE', classificacao: 'B', ordem: 4, meta_valor: 2_000_000, bonus_percentual: 20 },
  ],
  POS_VENDA: [
    { nivel: 'DIAMANTE', classificacao: 'AAA', ordem: 1, meta_fixacao: 60, bonus_percentual: 60 },
    { nivel: 'OURO', classificacao: 'A', ordem: 2, meta_fixacao: 40, bonus_percentual: 40 },
    { nivel: 'PLATINUM', classificacao: 'BB', ordem: 3, meta_fixacao: 30, bonus_percentual: 30 },
    { nivel: 'BRONZE', classificacao: 'B', ordem: 4, meta_fixacao: 20, bonus_percentual: 20 },
  ],
};

// Comissão fixa confirmada pela cliente em 15/jul/2026.
// Os bônus das faixas são premiações independentes e não alteram estes percentuais.
export const TIPO_DESEMPENHO_DEFAULTS: Record<
  TipoDesempenho,
  Partial<DesempenhoConfigFormData>
> = {
  RELACIONADOR: {
    comissao_percentual_base: 0.03,
  },
  LIDER_VENDAS: {
    comissao_percentual_base: 0.04,
  },
  SUPERVISOR: {
    comissao_percentual_base: 0.2,
  },
  POS_VENDA: {
    comissao_percentual_base: 0.1,
    comissao_gerente_pos_percentual: 0.05,
  },
  REPRESENTANTE: {
    comissao_percentual_base: 4.2,
    comissao_primeira_parcela_percentual: 2.2,
    comissao_parcelas_2_a_5_percentual: 0.5,
  },
  ASSOCIADO: {
    meta_valor: 500000,
    // Ainda não confirmado pela cliente.
    comissao_percentual_base: 3,
  },
};

// Qual meta mostrar por tipo?
export const CAMPOS_META_POR_TIPO: Record<TipoDesempenho, {
  valor: boolean;
  reunioes: boolean;
  fixacao: boolean;
}> = {
  RELACIONADOR: { valor: true, reunioes: true, fixacao: false },
  LIDER_VENDAS: { valor: true, reunioes: false, fixacao: false },
  SUPERVISOR: { valor: true, reunioes: false, fixacao: false },
  POS_VENDA: { valor: false, reunioes: false, fixacao: true },
  REPRESENTANTE: { valor: true, reunioes: false, fixacao: false },
  ASSOCIADO: { valor: true, reunioes: false, fixacao: false },
};
